import React, { useEffect, useRef, useState } from "react";
import styles from "./VideoPlayer.module.css";
import { Caption, SpecialEffect, MediaItem } from "../../types";
import "./EffectsPreview.css";
import { getMediaTransform, getTransformOrigin } from "./mediaTransformUtils";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/free-solid-svg-icons';
import { buildColorFilterCSS, buildMediaItemFilterCSS } from '../../utils/colorFilterUtils';

interface VideoDisplayProps {
  video?: string;
  mediaItems: MediaItem[];
  videoRef: React.RefObject<HTMLVideoElement>;
  isVerticalVideo: boolean;
  isPlaying: boolean;
  currentTime: number; // for video mode
  mediaCurrentTime: number; // for media mode
  vertFontRatio: number;
  activeCaptions?: Caption[];
  onMediaTimeUpdate?: (time: number) => void;
  onCaptionDrag?: (captionId: string, newYPosition: number, newXPosition?: number) => void; // NEW: callback for caption dragging
  onCaptionClick?: (captionId: string) => void; // NEW: callback for caption click to open editor
  onCanvasDimensionsChange?: (dimensions: { width: number; height: number }) => void; // NEW: track canvas dimensions
  // NEW: Conversion mode props
  isConversionMode?: boolean;
  onUpdateMediaPosition?: (itemId: string, deltaX: number, deltaY: number) => void;
  onUpdateMediaScale?: (itemId: string, scaleDelta: number) => void;
  onResetMediaTransform?: (itemId: string) => void; // NEW: reset position and scale
  aspectRatio?: string; // Output aspect ratio (final output format)
  targetAspectRatio?: string | null; // Target aspect ratio when in conversion mode
  shouldPlayVideoAudio?: boolean; // NEW: whether video should play its audio (video-only mode)
}

const VideoDisplay: React.FC<VideoDisplayProps> = ({
  video,
  mediaItems,
  videoRef,
  isVerticalVideo,
  isPlaying,
  currentTime,
  mediaCurrentTime,
  vertFontRatio,
  activeCaptions,
  onMediaTimeUpdate,
  onCaptionDrag, // NEW
  onCaptionClick, // NEW: caption click callback
  onCanvasDimensionsChange, // NEW
  isConversionMode, // NEW
  onUpdateMediaPosition, // NEW
  onUpdateMediaScale, // NEW
  onResetMediaTransform, // NEW
  aspectRatio, // Final output aspect ratio
  targetAspectRatio, // Target when in conversion mode
  shouldPlayVideoAudio = false, // NEW: default to false (muted)
}) => {
  // Determine which aspect ratio to use for transform logic
  // When in conversion mode, use targetAspectRatio; otherwise use aspectRatio
  const effectiveAspectRatio = isConversionMode ? targetAspectRatio : aspectRatio;
  // State for caption dragging
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartX, setDragStartX] = useState(0);
  const [originalYPosition, setOriginalYPosition] = useState(0);
  const [originalXPosition, setOriginalXPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // NEW: State for media dragging (conversion mode)
  const [isMediaDragging, setIsMediaDragging] = useState(false);
  const [mediaDragStart, setMediaDragStart] = useState({ x: 0, y: 0 });
  
  // NEW: Track seeking state to prevent time updates during seeks
  const [isSeeking, setIsSeeking] = useState(false);

  // NEW: Track actual video dimensions for accurate preview
  const [videoDimensions, setVideoDimensions] = useState<{width: number, height: number} | null>(null);

  // NEW: Track actual rendered container dimensions for font scaling
  const [containerDimensions, setContainerDimensions] = useState<{width: number, height: number} | null>(null);

  // Calculate font scale factor based on container vs server dimensions
  const getFontScaleFactor = () => {
    // If aspectRatio not set yet, return default scale
    if (!aspectRatio) {
      return 0.230; // Default for 9:16 until aspect ratio is determined
    }
    
    if (aspectRatio !== '9:16') {
      return 1; // No scaling for non-9:16
    }
    
    if (!containerDimensions) {
      return 0.230;
    }
    
    // Scale fonts proportionally with container size
    const serverWidth = 450;
    const proportionalScale = (containerDimensions.width / serverWidth) * 0.42;
    
    return proportionalScale;
  };

  const fontScaleFactor = getFontScaleFactor();

  // NEW: Get video natural dimensions when loaded
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && video) {
      const handleLoadedMetadata = () => {
        setVideoDimensions({
          width: videoElement.videoWidth,
          height: videoElement.videoHeight
        });
      };
      
      if (videoElement.readyState >= 1) {
        handleLoadedMetadata();
      }
      
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    }
  }, [video, videoRef]);

  // NEW: Track canvas dimensions
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const dimensions = {
          width: rect.width,
          height: rect.height
        };
        
        // Update local state for font scaling
        setContainerDimensions(dimensions);
        
        // Also call parent callback if provided
        if (onCanvasDimensionsChange) {
          onCanvasDimensionsChange(dimensions);
        }
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => window.removeEventListener('resize', updateDimensions);
  }, [onCanvasDimensionsChange]);

  const containerStyle: React.CSSProperties = isVerticalVideo
    ? { width: `100%`, margin: "0 auto", position: "relative" }//{ width: `${vertFontRatio}%`, margin: "0 auto", position: "relative" }
    : { width: "100%", position: "relative" };

  const cpationShadowAJusment = 0.3;
  // For media mode, determine the active media item
  // Filter out hidden items from display
  const visibleMediaItems = mediaItems.filter(item => !item.hidden);
  
  const activeMedia =
    !video && visibleMediaItems.length > 0
      ? visibleMediaItems.find(
          (item) =>
            mediaCurrentTime >= item.start && mediaCurrentTime < item.end
        )
      : null;

  // For media mode video items, use a local ref.
  const mediaVideoRef = useRef<HTMLVideoElement>(null);

  // Caption drag handlers
  const handleCaptionMouseDown = (e: React.MouseEvent, captionId: string, currentYPosition: number, currentXPosition?: number) => {
    e.preventDefault();
    setIsDragging(captionId);
    setDragStartY(e.clientY);
    setDragStartX(e.clientX);
    setOriginalYPosition(currentYPosition);
    setOriginalXPosition(currentXPosition || 50);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current || !onCaptionDrag) return;

    const containerHeight = containerRef.current.offsetHeight;
    const containerWidth = containerRef.current.offsetWidth;
    const deltaY = e.clientY - dragStartY;
    const deltaX = e.clientX - dragStartX;
    
    console.log('=== Position Debug ===');
    console.log('Container:', containerWidth, 'x', containerHeight);
    console.log('Mouse deltas:', deltaX, deltaY);
    console.log('Original positions:', originalXPosition, originalYPosition);
    
    // Convert pixel movement to percentage for Y (0-100% from bottom)
    const yPercentChange = -(deltaY / containerHeight) * 100;
    let newYPosition = originalYPosition + yPercentChange;
    
    // Convert pixel movement to percentage for X (0-100% from left)
    const xPercentChange = (deltaX / containerWidth) * 100;
    let newXPosition = originalXPosition + xPercentChange;
    
    // Clamp to valid ranges
    newYPosition = Math.max(0, Math.min(100, newYPosition));
    newXPosition = Math.max(0, Math.min(100, newXPosition));
    
    console.log('New positions:', newXPosition.toFixed(1), newYPosition.toFixed(1));
    console.log('=======================');
    
    onCaptionDrag(isDragging, newYPosition, newXPosition);
  };

  const handleMouseUp = () => {
    setIsDragging(null);
    setDragStartY(0);
    setDragStartX(0);
    setOriginalYPosition(0);
    setOriginalXPosition(0);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStartY, dragStartX, originalYPosition, originalXPosition]);

  // NEW: Mouse handlers for media adjustment (conversion mode)
  const handleMediaMouseDown = (e: React.MouseEvent) => {
    if (!isConversionMode || !activeMedia) return;
    e.preventDefault();
    setIsMediaDragging(true);
    setMediaDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMediaWheel = (e: React.WheelEvent) => {
    if (!isConversionMode || !activeMedia || !onUpdateMediaScale) return;
    e.preventDefault();
    
    // Scroll up = zoom in (positive), scroll down = zoom out (negative)
    const scaleDelta = e.deltaY > 0 ? -0.05 : 0.05;
    onUpdateMediaScale(activeMedia.id!, scaleDelta);
  };

  // NEW: Global mouse handlers for media dragging
  useEffect(() => {
    if (!isMediaDragging || !activeMedia || !onUpdateMediaPosition) return;

    const handleMediaMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - mediaDragStart.x;
      const deltaY = e.clientY - mediaDragStart.y;
      
      // Convert pixel movement to percentage (relative to container size)
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const deltaXPercent = (deltaX / rect.width) * 100;
        const deltaYPercent = (deltaY / rect.height) * 100;
        
        onUpdateMediaPosition(activeMedia.id!, deltaXPercent, deltaYPercent);
        setMediaDragStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMediaMouseUp = () => {
      setIsMediaDragging(false);
    };

    document.addEventListener('mousemove', handleMediaMouseMove);
    document.addEventListener('mouseup', handleMediaMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMediaMouseMove);
      document.removeEventListener('mouseup', handleMediaMouseUp);
    };
  }, [isMediaDragging, mediaDragStart, activeMedia, onUpdateMediaPosition]);

  // Attach timeupdate listener for media video items
  useEffect(() => {
    if (
      activeMedia &&
      activeMedia.type === "video" &&
      mediaVideoRef.current &&
      onMediaTimeUpdate
    ) {
      const handleMediaTimeUpdate = () => {
        // Skip time updates while seeking
        if (isSeeking) {
          return;
        }
        
        const videoCurrentTime = mediaVideoRef.current!.currentTime;
        const videoOffset = activeMedia.videoOffset || 0;
        const relativeTime = videoCurrentTime - videoOffset;
        const absoluteTime = activeMedia.start + relativeTime;
        
        // Ignore negative or far-future timeline values
        if (absoluteTime < 0 || absoluteTime > activeMedia.end + 1) {
          return;
        }
        
        onMediaTimeUpdate(absoluteTime);
      };
      const mediaEl = mediaVideoRef.current;
      mediaEl.addEventListener("timeupdate", handleMediaTimeUpdate);
      return () => {
        mediaEl.removeEventListener("timeupdate", handleMediaTimeUpdate);
      };
    }
  }, [activeMedia, onMediaTimeUpdate]);

  // Control play/pause and sync for active media video
  useEffect(() => {
    if (
      activeMedia &&
      activeMedia.type === "video" &&
      mediaVideoRef.current
    ) {
      const mediaEl = mediaVideoRef.current;
      
      const relativeTimeFromStart = mediaCurrentTime - activeMedia.start;
      const videoOffset = activeMedia.videoOffset || 0;
      const videoDuration = activeMedia.videoDuration || activeMedia.length;
      const desiredTime = videoOffset + relativeTimeFromStart;
      
      // Safety check: Only seek if we're within the segment's timeline bounds
      const isWithinSegmentBounds = mediaCurrentTime >= activeMedia.start && mediaCurrentTime <= activeMedia.end;
      
      // Only seek if we're within bounds and out of sync
      if (isWithinSegmentBounds && Math.abs(mediaEl.currentTime - desiredTime) > 0.1) {
        if (mediaEl.readyState >= 1) {
          setIsSeeking(true);
          
          const handleSeeked = () => {
            setIsSeeking(false);
            mediaEl.removeEventListener('seeked', handleSeeked);
          };
          
          mediaEl.addEventListener('seeked', handleSeeked);
          mediaEl.currentTime = desiredTime;
          
          // Fallback timeout
          setTimeout(() => {
            if (isSeeking) {
              setIsSeeking(false);
            }
          }, 1000);
        } else {
          // Wait for metadata to load
          const handleLoadedMetadata = () => {
            setIsSeeking(true);
            mediaEl.currentTime = desiredTime;
            mediaEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
            
            const handleSeeked = () => {
              setIsSeeking(false);
              mediaEl.removeEventListener('seeked', handleSeeked);
            };
            mediaEl.addEventListener('seeked', handleSeeked);
          };
          mediaEl.addEventListener('loadedmetadata', handleLoadedMetadata);
        }
      }
      // Only call play if we’re meant to be playing, element is paused, and ready
      if (isPlaying && mediaEl.paused && mediaEl.readyState >= 3) {
        // Delay slightly to ensure load completes before play is invoked
        setTimeout(() => {
          mediaEl
            .play()
            .catch((err) => {
              if (err.name !== "AbortError") {
                console.error("Play failed", err);
              } else {
                console.log("Play() aborted due to new load request, ignoring.");
              }
            });
        }, 100);
      } else if (!isPlaying && !mediaEl.paused) {
        mediaEl.pause();
      }
    }
  }, [mediaCurrentTime, activeMedia, isPlaying]);

  // Helper function to calculate container dimensions based on aspect ratio
  const getContainerDimensions = () => {
    // For 9:16 aspect ratio, use smaller max-width for better viewport fit
    if (aspectRatio === '9:16') {
      return {
        maxWidth: '280px',
        width: '100%',
        height: 'auto'
      };
    }
    
    // If aspectRatio not set yet, assume 9:16 for now (will adjust once detected)
    // This prevents flash of large text on 9:16 videos
    if (!aspectRatio) {
      return {
        maxWidth: '280px',
        width: '100%',
        height: 'auto'
      };
    }
    
    // For other aspect ratios, use auto sizing
    return { height: 'auto', width: '100%' };
  };

  const containerDims = getContainerDimensions();

    return (
    <div 
      className={styles.videoContainer} 
      style={{
        aspectRatio: aspectRatio === '9:16' ? '9 / 16' : 
        aspectRatio === '1:1' ? '1 / 1' : 
        '16 / 9',
        width: containerDims.width,
        height: containerDims.height,
        maxWidth: containerDims.maxWidth,
        cursor: isConversionMode && activeMedia ? 'move' : 'default',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
        display: 'block'
      }} 
      ref={containerRef}
      onMouseDown={handleMediaMouseDown}
      onWheel={handleMediaWheel}
    >
      {/* Reset Button - Top Right Corner */}
      {isConversionMode && activeMedia && onResetMediaTransform && (activeMedia.position || activeMedia.scale) && (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering drag
            onResetMediaTransform(activeMedia.id!);
          }}
          title="Reset position and scale to default"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1000,
            padding: '8px 12px',
            backgroundColor: 'rgba(239, 68, 68, 0.9)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.9)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.9)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <FontAwesomeIcon icon={faUndo} />
          Reset
        </button>
      )}
      
      {video ? (
        <video 
          ref={videoRef} 
          src={video} 
          className={styles.videoElement}
          style={{
            objectFit: aspectRatio === '9:16' ? 'cover' : 'contain'
          }}
        />
      ) : mediaItems.length > 0 ? (
        activeMedia ? (
          activeMedia.type === "video" ? (
            <div className="effectWrapper" style={{ position: 'relative', width: '100%', height: '100%' }}>
              <div className={`effectItem`} style={{ width: '100%', height: '100%' }}> {/* ${activeMedia.effect ? `effect-${activeMedia.effect}` : ''} */}
                <video
                  key={activeMedia.id} // Force remount when switching between segments
                  ref={mediaVideoRef}
                  src={activeMedia.url}
                  muted={!shouldPlayVideoAudio} // NEW: Only mute if not in video-audio mode
                  className={`${styles.videoElement} effectMedia`}
                  style={{
                    objectFit: aspectRatio === '9:16' ? 'cover' : 'contain',
                    width: '100%',
                    height: '100%',
                    // Apply transforms when:
                    // 1. In conversion/crop mode (for live editing), OR
                    // 2. Media has position/scale adjustments (to persist changes)
                    ...((isConversionMode || activeMedia.position || activeMedia.scale) && {
                      transform: getMediaTransform(activeMedia),
                      transformOrigin: getTransformOrigin(),
                      transition: 'transform 0.1s ease-out'
                    }),
                    // NEW: Apply all video effects for real-time preview
                    ...(buildMediaItemFilterCSS(activeMedia) && {
                      filter: buildMediaItemFilterCSS(activeMedia)
                    })
                  }}
                />
              </div>
              {/* Vignette overlay */}
              {activeMedia.vignette !== undefined && activeMedia.vignette > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none',
                  background: `radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,${0.3 * activeMedia.vignette}) 70%, rgba(0,0,0,${0.6 * activeMedia.vignette}) 100%)`,
                  zIndex: 1
                }} />
              )}
            </div>
          ) : (
            <div className="effectWrapper" style={{ position: 'relative', width: '100%', height: '100%' }}>
              <div className={`effectItem`} style={{ width: '100%', height: '100%' }}> {/* ${activeMedia.effect ? `effect-${activeMedia.effect}` : ''} */}
                <img
                  src={activeMedia.src}
                  alt={activeMedia.name}
                  className={`${styles.mediaImage} effectMedia`}
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: aspectRatio === '9:16' ? 'cover' : 'contain',
                    // Apply transforms when:
                    // 1. In conversion/crop mode (for live editing), OR
                    // 2. Media has position/scale adjustments (to persist changes)
                    ...((isConversionMode || activeMedia.position || activeMedia.scale) && {
                      transform: getMediaTransform(activeMedia),
                      transformOrigin: getTransformOrigin(),
                      transition: 'transform 0.1s ease-out'
                    }),
                    // NEW: Apply all video effects for real-time preview
                    ...(buildMediaItemFilterCSS(activeMedia) && {
                      filter: buildMediaItemFilterCSS(activeMedia)
                    })
                  }}
                />
              </div>
              {/* Vignette overlay for images */}
              {activeMedia.vignette !== undefined && activeMedia.vignette > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none',
                  background: `radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,${0.3 * activeMedia.vignette}) 70%, rgba(0,0,0,${0.6 * activeMedia.vignette}) 100%)`,
                  zIndex: 1
                }} />
              )}
            </div>
          )
        ) : (
          <div className={styles.noMedia}>No active media</div>
        )
      ) : (
        <div className={styles.noMedia}>No video or media available</div>
      )}

      {/* Caption display on video player */}
      {activeCaptions && containerDimensions && aspectRatio &&
        activeCaptions.map((caption) => (
          <div
            key={caption.id}
            className={`${styles.captionOverlay} ${isDragging === caption.id ? styles.dragging : ''}`}
            style={{
              bottom: (() => {
                const yPos = caption.style.yPosition !== undefined ? caption.style.yPosition : 0;
                
                // For 9:16 aspect ratio, adjust position down slightly
                if (aspectRatio === '9:16') {
                  // Reduce by 3% to move text lower (closer to bottom)
                  const adjustedPos = yPos - 3;
                  return `${Math.max(adjustedPos, 0)}%`; // Don't go below 0
                }
                
                // For non-9:16, use the old title-specific adjustments
                if (caption.itemType === 'titles' && yPos > 40 && yPos <= 80) {
                  return `${yPos*0.87}%`;
                }
                if (caption.itemType === 'titles' && yPos > 80 ) {
                  return `${yPos*0.92}%`;
                }
                if (caption.itemType === 'titles' && yPos <= 10 ) {
                  return `${yPos*0.5}%`;
                } 
               
                return `${yPos}%`;
              })(),
              left: `${caption.style.xPosition || 50}%`,
              transform: 'translateX(-50%)', // Center the text horizontally
              fontSize: (() => {
                let baseFontSize = caption.style.fontSize;
                
                // For 9:16 aspect ratio, don't apply vertical video scaling
                // since we're using fixed dimensions that match the server
                if (aspectRatio !== '9:16' && isVerticalVideo) {
                  baseFontSize = baseFontSize - (((100 - vertFontRatio) * baseFontSize) / 100);
                }
                
                // Apply font scale factor for 9:16 aspect ratio
                return baseFontSize * fontScaleFactor;
              })(),
              fontFamily: caption.style.fontFamily,
              textShadow: `${caption.style.shadowxz?caption.style.shadowxz*cpationShadowAJusment:0}px ${caption.style.shadowxz?caption.style.shadowxz*cpationShadowAJusment:0}px ${caption.style.shadowxz?caption.style.shadowxz*cpationShadowAJusment:0}px ${caption.style.shadowColor}`,
              color: caption.style.color,
              WebkitTextStroke: `${caption.style.strokeWidth}px  ${caption.style.stroke}`, // Add text border (outline)
              cursor: onCaptionDrag ? 'move' : 'default', // Show move cursor when dragging is enabled
              userSelect: 'none', // Prevent text selection while dragging
            }}
            onMouseDown={(e) => {
              // Call caption click handler to open the editor tab
              if (onCaptionClick) {
                onCaptionClick(caption.id);
              }
              
              // Also handle dragging if enabled
              if (onCaptionDrag) {
                handleCaptionMouseDown(e, caption.id, caption.style.yPosition || 20, caption.style.xPosition || 50);
              }
            }}
          >
            {caption.text}
          </div>
        ))}
    </div>
  );
};

export default VideoDisplay;
